import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, CheckCircle2, Ban, XCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResourceTable } from "@/components/data/ResourceTable";
import { MotifDialog } from "@/components/data/Dialogs";
import { PiecesJointes } from "@/components/data/Network";
import { PageHeader } from "@/components/data/StatusBadge";
import { PaiementDialog, PaiementsList } from "@/components/charges/ChargeDialogs";
import { Echeances, ChargesRapport } from "@/components/charges/ChargeViews";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const MOTIF = {
  approuver: { title: "Approuver la charge", label: "Approuver", ok: "Charge approuvée", required: false, desc: "La charge pourra ensuite être payée. Aucun mouvement de trésorerie n'est généré." },
  rejeter: { title: "Rejeter la charge", label: "Rejeter", ok: "Charge rejetée", required: true, desc: "La charge sera close sans impact sur la trésorerie." },
  annuler: { title: "Annuler la charge", label: "Annuler la charge", ok: "Charge annulée", required: true, desc: "Possible uniquement si aucun paiement n'a été enregistré." },
};

export default function ChargesPage() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const [dlg, setDlg] = useState({ type: null, row: null });
  const [reload, setReload] = useState(0);
  const done = () => setReload((n) => n + 1);
  const open = (type) => (row) => setDlg({ type, row });
  const close = () => setDlg({ type: null, row: null });
  const auto = (r) => r.origine === "virement";
  const extra = (row) => [
    { key: "approuver", label: "Approuver", icon: CheckCircle2, show: can("charges", "valider") && row.statut === "engagee" && !auto(row), onClick: open("approuver") },
    { key: "rejeter", label: "Rejeter", icon: Ban, show: can("charges", "valider") && row.statut === "engagee" && !auto(row), onClick: open("rejeter") },
    { key: "payer", label: "Enregistrer un paiement", icon: Banknote, show: can("charges", "valider") && ["approuvee", "partiellement_payee"].includes(row.statut) && !auto(row), onClick: open("payer") },
    { key: "annuler-charge", label: "Annuler la charge", icon: XCircle, show: can("charges", "annuler") && ["engagee", "approuvee"].includes(row.statut) && !Number(row.montant_paye) && !auto(row), onClick: open("annuler") },
  ];
  const payExtra = (row) => [
    { key: "annuler-paiement", label: "Annuler le paiement", icon: XCircle, show: can("charges", "annuler") && row.statut === "valide" && !row.sans_mouvement, onClick: open("annuler-paiement") },
  ];
  const m = MOTIF[dlg.type];
  const confirm = async (motif) => {
    const url = dlg.type === "annuler-paiement" ? `/charges/paiements/${dlg.row.id}/annuler` : `/charges/${dlg.row.id}/${dlg.type}`;
    await api.post(url, { motif });
    toast.success(m ? m.ok : "Paiement annulé : écriture contre-passée");
    done();
  };
  return (
    <div>
      <PageHeader title="Charges & dépenses" subtitle="Engagée → approuvée → partiellement payée → payée. Une charge engagée ou approuvée ne réduit pas la trésorerie : seuls les paiements enregistrés débitent un compte." />
      <Tabs defaultValue={params.get("tab") || "charges"}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="charges" data-testid="tab-charges">Charges</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-charge-paiements">Paiements</TabsTrigger>
          <TabsTrigger value="echeances" data-testid="tab-echeances">Échéances</TabsTrigger>
          <TabsTrigger value="rapports" data-testid="tab-charges-rapports">Rapports</TabsTrigger>
        </TabsList>
        <TabsContent value="charges">
          <ResourceTable res="charges" extraActions={extra} reloadKey={reload} onChanged={done}
            detailExtra={(row) => <><PaiementsList row={row} /><PiecesJointes res="charges" entityId={row.id} /></>} />
        </TabsContent>
        <TabsContent value="paiements"><ResourceTable res="charge_paiements" extraActions={payExtra} reloadKey={reload} /></TabsContent>
        <TabsContent value="echeances"><Echeances reloadKey={reload} /></TabsContent>
        <TabsContent value="rapports"><ChargesRapport /></TabsContent>
      </Tabs>
      {dlg.type === "payer" && <PaiementDialog charge={dlg.row} onClose={close} onDone={done} />}
      <MotifDialog open={!!m || dlg.type === "annuler-paiement"} onOpenChange={(o) => !o && close()}
        title={m ? `${m.title} ${dlg.row?.numero || ""}` : `Annuler le paiement ${dlg.row?.numero || ""}`}
        description={m ? m.desc : "Le débit du compte sera contre-passé et le reste à payer de la charge recalculé."}
        confirmLabel={m ? m.label : "Annuler le paiement"} required={m ? m.required : true} onConfirm={confirm} />
    </div>
  );
}
